import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import EyeIcons from '@/icons/EyeIcons'
import SendIcons from '@/icons/SendIcons'
import { PreviewServiceNotificationModalProps } from '@/modals/ServiceModals/PreviewServiceNotification'
import { deliveryNotificationMailer } from '@/modules/Service/services'
import {
    ServiceCustomerMailer,
    ServiceNotification,
} from '@/modules/Service/types'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Ticket } from 'lucide-react'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { GridHookForm, Typogaphy } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface ServiceNotificationMailerProps {
    mailers: ServiceCustomerMailer[]
    notifications: ServiceNotification[]
    serviceID: number
    onClose: () => void
    referrerTicketId?: number | null
    defaultNotificationId?: number | null
}

interface NotificationForm {
    customer_mailer_id: number
    mailer_cc: string
    mailer_bcc: string
    mailer_receiver: string
    service_system_information_id: number
}

const ServiceNotificationMailer: React.FC<ServiceNotificationMailerProps> = ({
    mailers,
    notifications = [],
    serviceID,
    onClose,
    referrerTicketId,
    defaultNotificationId,
}) => {
    const { setModal } = useModal()
    const { t } = useTranslation()

    const resolvedNotificationId =
        defaultNotificationId ??
        notifications?.[0]?.service_system_information_id ??
        -1

    const formControl = useForm<NotificationForm>({
        defaultValues: mailers?.[0]
            ? {
                  customer_mailer_id: mailers?.[0]?.customer_mailer_id,
                  mailer_cc: mailers?.[0]?.mailer_cc,
                  mailer_bcc: mailers?.[0]?.mailer_bcc,
                  mailer_receiver: mailers?.[0]?.mailer_receiver,
                  service_system_information_id: resolvedNotificationId,
              }
            : {
                  customer_mailer_id: -1,
                  mailer_cc: '',
                  mailer_bcc: '',
                  mailer_receiver: '',
                  service_system_information_id: resolvedNotificationId,
              },
    })

    const mailerReceiver = formControl.watch('mailer_receiver')
    const notificationId = formControl.watch('service_system_information_id')
    const isFormIncomplete =
        !mailerReceiver || !notificationId || notificationId === -1

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: deliveryNotificationMailer,
        },
        {
            successMessage: t('delivery-notification-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
            onSuccess: () => {
                onClose()
            },
        }
    )

    const onSubmit = async (values: NotificationForm) => {
        return mutateAsync({
            serviceID,
            notificationID: values.service_system_information_id,
            mailerID: values?.customer_mailer_id,
            mailerParams: {
                mailer_receivers: values?.mailer_receiver,
                mailer_cc: values?.mailer_cc,
                mailer_bcc: values?.mailer_bcc,
                ...(referrerTicketId && { ticket_id: referrerTicketId }),
            },
        })
    }

    return (
        <div className="mt-2 p-4 bg-white rounded-lg">
            <Typogaphy className="font-medium mb-2 text-primary">
                {t('delivery-notification-mailer')}
            </Typogaphy>
            {/* Related Ticket Badge */}
            {referrerTicketId && (
                <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-blue-50 border border-blue-200">
                    <Ticket className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-blue-700">
                        {t('related-ticket')}:
                    </span>
                    <Link
                        to={generatePathDatacentral(paths.ticketDetail, {
                            id: referrerTicketId,
                        })}
                        target="_blank"
                        className="text-sm font-medium text-blue-600 hover:underline"
                    >
                        #{referrerTicketId}
                    </Link>
                </div>
            )}
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                    },
                    {
                        title: t('preview-notification'),
                        type: 'button',
                        variant: 'contained',
                        disabled: isPending || isFormIncomplete,
                        loading: isPending,
                        handler: () => {
                            setModal<PreviewServiceNotificationModalProps>(
                                'ServiceModals/PreviewServiceNotification/index',
                                {
                                    serviceID: serviceID,
                                    notificationID: formControl?.watch(
                                        `service_system_information_id`
                                    ),
                                    mailerID:
                                        formControl?.watch(
                                            `customer_mailer_id`
                                        ),
                                }
                            )
                        },
                        startIcon: (
                            <EyeIcons className="w-4 h-4 text-primary" />
                        ),
                    },
                    {
                        title: t('delivery-notification'),
                        type: 'submit',
                        variant: 'contained',
                        disabled: isPending || isFormIncomplete,
                        loading: isPending,
                        startIcon: <SendIcons />,
                    },
                ]}
                onSubmit={onSubmit}
                data={[
                    {
                        formItemProps: {
                            name: 'customer_mailer_id',
                            type: 'select',
                            allowClear: false,
                            label: t('customer-mailer'),
                            options: mailers?.map((notification) => ({
                                value: notification.customer_mailer_id,
                                label: notification.customer_mailer_name,
                            })),
                            placeholder: t('customer-mailer'),
                            onChangeAfterHookForm(value) {
                                const selectMailer = mailers?.find(
                                    (notification) =>
                                        notification?.customer_mailer_id ===
                                        (value as number)
                                )

                                if (selectMailer) {
                                    formControl.setValue(
                                        'mailer_receiver',
                                        selectMailer?.mailer_receiver || ''
                                    )
                                    formControl.setValue(
                                        'mailer_cc',
                                        selectMailer?.mailer_cc || ''
                                    )
                                    formControl.setValue(
                                        'mailer_bcc',
                                        selectMailer?.mailer_bcc || ''
                                    )
                                }
                            },
                        },
                        className: 'col-span-1',
                    },
                    {
                        formItemProps: {
                            name: 'mailer_receiver',
                            type: 'textfield',
                            label: t('mailer_receiver'),
                            placeholder: t('mailer_receiver'),
                        },
                        className: 'col-span-1',
                    },
                    {
                        formItemProps: {
                            name: 'mailer_cc',
                            type: 'textfield',
                            label: t('mailer_cc'),
                            placeholder: t('mailer_cc'),
                        },
                        className: 'col-span-1',
                    },
                    {
                        formItemProps: {
                            name: 'mailer_bcc',
                            type: 'textfield',
                            label: t('mailer_bcc'),
                            placeholder: t('mailer_bcc'),
                        },
                        className: 'col-span-1',
                    },
                    {
                        formItemProps: {
                            name: 'service_system_information_id',
                            type: 'select',
                            label: t('service_system_information_id'),
                            placeholder: t('service_system_information_id'),
                            allowClear: false,
                            options: notifications?.map((notification) => {
                                return {
                                    value: notification.service_system_information_id,
                                    label: notification.notification_name,
                                }
                            }),
                        },
                        className: 'col-span-1',
                    },
                ]}
            />
        </div>
    )
}

export default ServiceNotificationMailer
